import { useCallback, useSyncExternalStore } from 'react';
import type { User } from '../api/types';
import { canPickStore, defaultStoreFilter } from './roles';

/**
 * App-wide store ("Tienda") selection.
 *
 * One value shared by every page that honors a store filter, so the picker
 * in the header behaves like a global setting instead of resetting on each
 * navigation. Persisted per username in localStorage — a shared shop PC can
 * have an owner and a manager with different habits. Roles that can't pick
 * a store (staff locked to sid_assigned, accounting = whole chain) always
 * get their role default and their setter is a no-op, mirroring what the
 * daemon enforces server-side anyway.
 *
 * Implemented as a tiny external store (module state + subscribers) read
 * through useSyncExternalStore, so no provider has to be threaded through
 * main.tsx or every page test.
 */
const KEY_PREFIX = 'pacebi-store-filter:';

export function storeFilterKey(user: Pick<User, 'username'>): string {
  return KEY_PREFIX + user.username;
}

const cache = new Map<string, number | null>();
const listeners = new Set<() => void>();

function read(key: string): number | null {
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  let sid: number | null = null;
  try {
    const raw = localStorage.getItem(key);
    if (raw !== null) {
      const n = Number(raw);
      sid = Number.isInteger(n) && n > 0 ? n : null;
    }
  } catch { /* storage blocked — behave as "all stores" */ }
  cache.set(key, sid);
  return sid;
}

function write(key: string, sid: number | null): void {
  if (cache.get(key) === sid) return;
  cache.set(key, sid);
  try {
    if (sid === null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(sid));
  } catch { /* best-effort persistence */ }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

/** Non-hook read for one-off use outside React (e.g. building a URL). */
export function getStoreFilter(user: User): number | null {
  return canPickStore(user) ? read(storeFilterKey(user)) : defaultStoreFilter(user);
}

/** Non-hook write; ignored for roles that can't pick a store. */
export function setStoreFilter(user: User, sid: number | null): void {
  if (canPickStore(user)) write(storeFilterKey(user), sid);
}

export function useStoreFilter(user: User): [number | null, (sid: number | null) => void] {
  const key = storeFilterKey(user);
  const pick = canPickStore(user);
  const locked = defaultStoreFilter(user);
  const value = useSyncExternalStore(subscribe, () => (pick ? read(key) : locked));
  const set = useCallback(
    (sid: number | null) => { if (pick) write(key, sid); },
    [pick, key],
  );
  return [value, set];
}

/** Drop the in-memory copy so a test can exercise the storage read path. */
export function resetStoreFilterForTests(): void {
  cache.clear();
}
